import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { fetchData } from '../../apiService';
import useSessionManager from '../../hooks/useSessionManager';
import {
  Receipt,
  Payment,
  ShoppingCart,
  Add,
  Description,
  Undo,
  AccountBalance,
  AccountBalanceWallet,
  Close as CloseIcon
} from '@mui/icons-material';
import { CircularProgress, Dialog, DialogTitle, DialogContent, Box, Grid, Typography, Chip, Button, IconButton } from '@mui/material';
import SaleModal from '../pages/Sale/SaleModal';
import PurchaseModal from '../pages/Purchase/PurchaseModal';
import PurchaseReturnModal from '../pages/PurchaseReturn/PurchaseReturnModal';
import SaleReturnModal from '../pages/SaleReturn/SaleReturnModal';
import PaymentReceiptsModal from '../pages/PaymentReceipts/PaymentReceiptsModal';
import JournalEntryModal from '../pages/JournalEntry/JournalEntryModal';
import ModernLoadingOverlay from '../common/ModernLoadingOverlay';
import DashboardLoader from '../common/DashboardLoader';
import './Home.css';

const Home = () => {
  const { companyId, branchId, token } = useAuth();
  const { validateSession } = useSessionManager();
  const history = useHistory();
  const [loading, setLoading] = useState(true);

  const [transactionStats, setTransactionStats] = useState({
    sales: { count: 0, amount: 0 },
    purchases: { count: 0, amount: 0 },
    saleReturns: { count: 0, amount: 0 },
    purchaseReturns: { count: 0, amount: 0 },
    paymentReceipts: { count: 0, amount: 0 },
    journalEntries: { count: 0, amount: 0 }
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [dailySales, setDailySales] = useState([]);

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('view');
  const [modalType, setModalType] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // Purchase Return view modal states
  const [purchaseReturnViewModalOpen, setPurchaseReturnViewModalOpen] = useState(false);
  const [selectedPurchaseReturn, setSelectedPurchaseReturn] = useState(null);
  const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);

  // Sale Return view modal states
  const [saleReturnViewModalOpen, setSaleReturnViewModalOpen] = useState(false);
  const [selectedSaleReturn, setSelectedSaleReturn] = useState(null);
  const [saleReturnDetails, setSaleReturnDetails] = useState([]);

  // Modal data states
  const [formData, setFormData] = useState({});
  const [detailsData, setDetailsData] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]);

  // Master data for modals
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [masters, setMasters] = useState([]);
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  useEffect(() => {
    if (!validateSession()) {
      return;
    }
    fetchDashboardData();
  }, [validateSession]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

      const [
        salesResponse,
        purchasesResponse,
        saleReturnsResponse,
        purchaseReturnsResponse,
        paymentReceiptsResponse,
        journalEntriesResponse
      ] = await Promise.all([
        fetchData('SaleApi', `/ActiveList/${companyId}/${branchId || 1}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PurchaseReturnApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('PaymentReceiptsApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] })),
        fetchData('JournalEntryApi', `/ActiveList/${companyId}/${branchId || 1}/${firstDayOfMonth}/${lastDayOfMonth}`, token).catch(() => ({ response_data: [] }))
      ]);

      const salesData = salesResponse.response_data || salesResponse || [];
      const purchasesData = purchasesResponse.response_data || purchasesResponse || [];
      const saleReturnsData = saleReturnsResponse.response_data || saleReturnsResponse || [];
      const purchaseReturnsData = purchaseReturnsResponse.response_data || purchaseReturnsResponse || [];
      const paymentReceiptsData = paymentReceiptsResponse.response_data || paymentReceiptsResponse || [];
      const journalEntriesData = journalEntriesResponse.response_data || journalEntriesResponse || [];

      // Calculate transaction stats
      setTransactionStats({
        sales: {
          count: salesData.length,
          amount: salesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        purchases: {
          count: purchasesData.length,
          amount: purchasesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        saleReturns: {
          count: saleReturnsData.length,
          amount: saleReturnsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        purchaseReturns: {
          count: purchaseReturnsData.length,
          amount: purchaseReturnsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0)
        },
        paymentReceipts: {
          count: paymentReceiptsData.length,
          amount: paymentReceiptsData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || item.Amount) || 0), 0)
        },
        journalEntries: {
          count: journalEntriesData.length,
          amount: journalEntriesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || item.Amount) || 0), 0)
        }
      });

      // Build recent activities
      const activities = [];

      salesData.slice(0, 5).forEach(sale => {
        activities.push({
          type: 'sale',
          id: sale.Id,
          icon: <Receipt />,
          title: 'Sale Invoice',
          description: `${sale.BillNumber || sale.BillNo} - ${sale.MasterName || sale.CustomerName || 'Customer'}`,
          amount: parseFloat(sale.TotalAmount) || 0,
          date: sale.BillDate || sale.CreatedDate,
          time: sale.CreatedTime || new Date(sale.CreatedDate || sale.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#7c3aed'
        });
      });

      purchasesData.slice(0, 5).forEach(purchase => {
        activities.push({
          type: 'purchase',
          id: purchase.Id,
          icon: <ShoppingCart />,
          title: 'Purchase',
          description: `${purchase.BillNumber || purchase.BillNo} - ${purchase.MasterName || purchase.VendorName || 'Vendor'}`,
          amount: parseFloat(purchase.TotalAmount) || 0,
          date: purchase.BillDate || purchase.CreatedDate,
          time: purchase.CreatedTime || new Date(purchase.CreatedDate || purchase.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#f59e0b'
        });
      });

      saleReturnsData.slice(0, 3).forEach(saleReturn => {
        activities.push({
          type: 'salereturn',
          id: saleReturn.Id,
          icon: <Undo />,
          title: 'Sale Return',
          description: `${saleReturn.BillNumber || saleReturn.BillNo} - ${saleReturn.MasterName || saleReturn.CustomerName || 'Customer'}`,
          amount: parseFloat(saleReturn.TotalAmount) || 0,
          date: saleReturn.BillDate || saleReturn.CreatedDate,
          time: saleReturn.CreatedTime || new Date(saleReturn.CreatedDate || saleReturn.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#ef4444'
        });
      });

      purchaseReturnsData.slice(0, 3).forEach(purchaseReturn => {
        activities.push({
          type: 'purchasereturn',
          id: purchaseReturn.Id,
          icon: <Undo />,
          title: 'Purchase Return',
          description: `${purchaseReturn.BillNumber || purchaseReturn.BillNo} - ${purchaseReturn.MasterName || purchaseReturn.VendorName || 'Vendor'}`,
          amount: parseFloat(purchaseReturn.TotalAmount) || 0,
          date: purchaseReturn.BillDate || purchaseReturn.CreatedDate,
          time: purchaseReturn.CreatedTime || new Date(purchaseReturn.CreatedDate || purchaseReturn.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#10b981'
        });
      });

      paymentReceiptsData.slice(0, 3).forEach(payment => {
        activities.push({
          type: 'payment',
          id: payment.Id,
          icon: <Payment />,
          title: 'Payment/Receipt',
          description: `${payment.BillNumber || payment.BillNo} - ${payment.MasterName || payment.PartyName || 'Party'}`,
          amount: parseFloat(payment.TotalAmount || payment.Amount) || 0,
          date: payment.BillDate || payment.CreatedDate,
          time: payment.CreatedTime || new Date(payment.CreatedDate || payment.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#3b82f6'
        });
      });

      journalEntriesData.slice(0, 3).forEach(journal => {
        activities.push({
          type: 'journal',
          id: journal.Id,
          icon: <Description />,
          title: 'Journal Entry',
          description: `${journal.BillNumber || journal.BillNo} - ${journal.MasterName || journal.Narration || 'Entry'}`,
          amount: parseFloat(journal.TotalAmount || journal.Amount) || 0,
          date: journal.BillDate || journal.CreatedDate,
          time: journal.CreatedTime || new Date(journal.CreatedDate || journal.BillDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          color: '#8b5cf6'
        });
      });

      activities.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentActivities(activities.slice(0, 20));

      // Calculate daily sales for graph
      const salesByDay = {};
      const currentDate = new Date();
      const last30Days = [];

      // Initialize last 30 days
      for (let i = 29; i >= 0; i--) {
        const date = new Date(currentDate);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        last30Days.push(dateStr);
        salesByDay[dateStr] = 0;
      }

      // Aggregate sales by day
      salesData.forEach(sale => {
        const saleDate = new Date(sale.BillDate || sale.CreatedDate).toISOString().split('T')[0];
        if (salesByDay.hasOwnProperty(saleDate)) {
          salesByDay[saleDate] += parseFloat(sale.TotalAmount) || 0;
        }
      });

      // Convert to array for graph
      const dailySalesData = last30Days.map(date => ({
        date: date,
        amount: salesByDay[date],
        label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));

      setDailySales(dailySalesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `AED ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleCardClick = (type) => {
    const routes = {
      sale: '/SaleIndex',
      purchase: '/PurchaseIndex',
      salereturn: '/SaleReturnAgainstIndex',
      purchasereturn: '/PurchaseReturnIndex',
      payment: '/PaymentReceiptsIndex',
      journal: '/JournalEntryIndex'
    };
    history.push(routes[type]);
  };


  const handleReportCardClick = (type) => {
    const routes = {
      ledger: '/Ledger',
      cashbook: '/CashBook',
      // dss: '/SaleReturnAgainstIndex',
      // purchasereturn: '/PurchaseReturnIndex',
      // payment: '/PaymentReceiptsIndex',
      // journal: '/JournalEntryIndex'
    };
    history.push(routes[type]);
  };


  const handleCreateAccount = (type) => {
    if (type === 'client') {
      history.push('/ClientIndex');
    } else if (type === 'vendor') {
      history.push('/VendorIndex');
    }
  };

  const fetchMasterData = async (type) => {
    try {
      if (type === 'sale' || type === 'salereturn') {
        // Fetch customers and items for sale
        const [customersRes, itemsRes, banksRes, freightRes] = await Promise.all([
          fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token).catch(() => ({ response_data: [] })),
          fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token).catch(() => ({ response_data: [] }))
        ]);

        setCustomers(customersRes.response_data || customersRes || []);
        setItems(itemsRes.response_data || itemsRes || []);
        setBanks(banksRes.response_data || banksRes || []);

        const freightData = (freightRes.response_data || freightRes || []);
        const freight = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('freight')) : null;
        const packing = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('packing')) : null;
        setFreightPackingGST({ freight, packing });

      } else if (type === 'purchase' || type === 'purchasereturn') {
        // Fetch vendors and items for purchase
        const [vendorsRes, itemsRes, freightRes] = await Promise.all([
          fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('PurchaseApi', `/ItemStockListDetails/${companyId}/${branchId}`, token).catch(() => ({ response_data: [] })),
          fetchData('PurchaseOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token).catch(() => ({ response_data: [] }))
        ]);

        setVendors(vendorsRes.response_data || vendorsRes || []);
        setItems(itemsRes.response_data || itemsRes || []);

        const freightData = (freightRes.response_data || freightRes || []);
        const freight = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('freight')) : null;
        const packing = Array.isArray(freightData) ? freightData.find(item => item.Name?.toLowerCase().includes('packing')) : null;
        setFreightPackingGST({ freight, packing });

      } else if (type === 'payment') {
        // Fetch masters and banks for payment/receipt
        const [mastersRes, banksRes] = await Promise.all([
          fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token).catch(() => ({ response_data: [] })),
          fetchData('BankApi', `/${companyId}`, token).catch(() => ({ response_data: [] }))
        ]);

        setMasters(mastersRes.response_data || mastersRes || []);
        setBanks(banksRes.response_data || banksRes || []);

      } else if (type === 'journal') {
        // Fetch masters for journal entry
        const mastersRes = await fetchData('JournalEntryApi', `/Master/${companyId}`, token).catch(() => ({ response_data: [] }));
        setMasters(mastersRes.response_data || mastersRes || []);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleActivityClick = async (activity) => {
    setModalLoading(true);
    setModalType(activity.type);
    setModalAction('view');

    try {
      // Fetch master data first
      await fetchMasterData(activity.type);

      // Fetch specific record details
      let response;
      const apiMap = {
        sale: { api: 'SaleApi', endpoint: `/${activity.id}` },
        purchase: { api: 'PurchaseApi', endpoint: `/${activity.id}` },
        salereturn: { api: 'SaleReturnApi', endpoint: `/${activity.id}` },
        purchasereturn: { api: 'PurchaseReturnApi', endpoint: `/${activity.id}` },
        payment: { api: 'PaymentAndReceiptApi', endpoint: `/PaymentData/${activity.id}` },
        journal: { api: 'JournalEntryApi', endpoint: `/${activity.id}` }
      };

      const apiInfo = apiMap[activity.type];
      if (apiInfo) {
        response = await fetchData(apiInfo.api, apiInfo.endpoint, token);
        const data = response.response_data || response;
        
        // Format data based on type
        if (activity.type === 'sale') {
          const saleData = data.Sale || data;
          const details = data.SaleDetails || [];
          const productRelationDetails = data.SaleProductRelationDetails || [];

          setFormData({
            Id: saleData.Id,
            CompanyId: saleData.CompanyId || companyId,
            BranchId: saleData.BranchId || branchId,
            MasterId: saleData.MasterId,
            CustomerName: saleData.CustomerName || '',
            VATNumber: saleData.GSTNumber || saleData.VATNumber || '',
            SalesManId: saleData.SalesManId || null,
            StateId: saleData.StateId || null,
            BillNumber: saleData.BillNumber,
            BillPrefix: saleData.BillPrefix || '',
            FinYear: saleData.FinYear || '',
            BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            ClearingDate: saleData.ClearingDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            GstCalculationType: saleData.GstCalculationType || 1,
            GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
            Amount: saleData.Amount || 0,
            DiscountAmount: saleData.DiscountAmount || 0,
            TaxableAmount: saleData.TaxableAmount || 0,
            VATAmount: saleData.VATAmount || 0,
            FreightAmount: saleData.FreightAmount || 0,
            FreightVATRate: saleData.FreightVATRate || 0,
            FreightVATAmount: saleData.FreightVATAmount || 0,
            PackingAmount: saleData.PackingAmount || 0,
            PackingVATRate: saleData.PackingVATRate || 0,
            PackingVATAmount: saleData.PackingVATAmount || 0,
            RoundOff: saleData.RoundOff || 0,
            TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
            GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
            CashAmount: saleData.CashAmount || 0,
            UPIAmount: saleData.UPIAmount || 0,
            CardAmount: saleData.CardAmount || 0,
            BankId: saleData.BankId || null,
            PaidAmount: saleData.PaidAmount || 0,
            BalanceAmount: saleData.BalanceAmount || 0,
            Remarks: saleData.Remarks || '',
            DispatchFrom: saleData.DispatchFrom || '',
            DispatchAddress1: saleData.DispatchAddress1 || '',
            DispatchAddress2: saleData.DispatchAddress2 || '',
            DispatchLocation: saleData.DispatchLocation || '',
            DispatchPincode: saleData.DispatchPincode || '',
            ShipTo: saleData.ShipTo || '',
            ShipGSTNo: saleData.ShipGSTNo || '',
            ShipToAddress1: saleData.ShipToAddress1 || '',
            ShipToAddress2: saleData.ShipToAddress2 || '',
            ShipToLocation: saleData.ShipToLocation || '',
            ShipToPincode: saleData.ShipToPincode || '',
            VehicleNumber: saleData.VehicleNumber || '',
            TransporterName: saleData.TransporterName || '',
            TransporterVAT: saleData.TransporterVAT || '',
            Distance: saleData.Distance || 0,
            IsVerified: saleData.IsVerified || false,
            IsActive: saleData.IsActive ?? true,
            CreatedBy: saleData.CreatedBy,
            CreatedDate: saleData.CreatedDate,
            CreatedByName: saleData.CreatedByName,
            ModifiedBy: saleData.ModifiedBy,
            ModifiedDate: saleData.ModifiedDate,
            ModifiedByName: saleData.ModifiedByName
          });

          setDetailsData(details.map(d => {
            const quantity = d.Quantity || 0;
            const rate = d.Rate || 0;
            const calculatedAmount = quantity * rate;

            return {
              Id: d.Id || 0,
              ItemId: d.ItemId,
              ItemName: d.ItemName || '',
              HSNCode: d.HSNCode || '',
              VATRate: d.VATRate || d.GSTRate || 0,
              GSTRate: d.GSTRate || 0,
              Quantity: quantity,
              Rate: rate,
              Amount: d.Amount || calculatedAmount,
              DiscountPercentage: d.DiscountPercentage || 0,
              DiscountAmount: d.DiscountAmount || 0,
              TaxableAmount: d.TaxableAmount || 0,
              VATAmount: d.VATAmount || 0,
              TotalAmount: d.TotalAmount || 0,
              IsActive: d.IsActive ?? true,
              CreatedBy: d.CreatedBy,
              CreatedDate: d.CreatedDate,
              ModifiedBy: d.ModifiedBy,
              ModifiedDate: d.ModifiedDate
            };
          }));

          // Transform product relation details into relatedItems structure
          const relatedItemsMap = {};
          productRelationDetails.forEach(rel => {
            const key = `${rel.ProductItemId}_${rel.SaleDetailId}`;
            if (!relatedItemsMap[key]) {
              relatedItemsMap[key] = {
                productId: rel.ProductItemId,
                productName: rel.ProductItemName || 'Product',
                productQuantity: rel.ProductQuantity || 1,
                saleDetailId: rel.SaleDetailId,
                items: []
              };
            }
            relatedItemsMap[key].items.push({
              Id: rel.Id,
              ItemId: rel.RelatedItemId,
              ItemName: rel.RelatedItemName || '',
              Quantity: rel.Quantity || 0,
              Qty: rel.Quantity || 0,
              Rate: rel.Rate || 0,
              SaleRate: rel.Rate || 0,
              Amount: (rel.Quantity || 0) * (rel.Rate || 0)
            });
          });

          setRelatedItems(Object.values(relatedItemsMap));

        } else if (activity.type === 'purchase') {
          const purchaseData = data.Purchase || data;
          const details = data.PurchaseDetails || [];

          setFormData({
            Id: purchaseData.Id,
            CompanyId: purchaseData.CompanyId || companyId,
            BranchId: purchaseData.BranchId || branchId,
            MasterId: purchaseData.MasterId,
            MasterName: purchaseData.MasterName || purchaseData.VendorName || '',
            InvoiceTypeId: purchaseData.InvoiceTypeId || 0,
            InvoiceTypeName: purchaseData.InvoiceTypeName || '',
            VATNumber: purchaseData.VATNumber || purchaseData.GSTNumber || '',
            VATCalculationType: purchaseData.VATCalculationType || 1,
            VATCalculationTypeName: purchaseData.VATCalculationTypeName || 'Excluding Tax',
            PurchaseOrderId: purchaseData.PurchaseOrderId || null,
            PurchaseOrderNo: purchaseData.PurchaseOrderNo || '',
            PurchaseOrderDate: purchaseData.PurchaseOrderDate?.split('T')[0] || '',
            BillDate: purchaseData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            ClearingDate: purchaseData.ClearingDate?.split('T')[0] || '',
            BillNumber: purchaseData.BillNumber || '',
            TotalQuantity: purchaseData.TotalQuantity || 0,
            Amount: purchaseData.Amount || 0,
            VATAmount: purchaseData.VATAmount || 0,
            TaxableAmount: purchaseData.TaxableAmount || 0,
            FreightAmount: purchaseData.FreightAmount || 0,
            FreightVATRate: purchaseData.FreightVATRate || purchaseData.FreightGSTRate || 0,
            FreightVATAmount: purchaseData.FreightVATAmount || purchaseData.FreightGSTAmount || 0,
            PackingAmount: purchaseData.PackingAmount || 0,
            PackingVATRate: purchaseData.PackingVATRate || purchaseData.PackingGSTRate || 0,
            PackingVATAmount: purchaseData.PackingVATAmount || purchaseData.PackingGSTAmount || 0,
            TCSAmount: purchaseData.TCSAmount || 0,
            DiscountAmount: purchaseData.DiscountAmount || 0,
            RoundOff: purchaseData.RoundOff || 0,
            TotalAmount: purchaseData.TotalAmount || 0,
            PaidAmount: purchaseData.PaidAmount || 0,
            BalanceAmount: purchaseData.BalanceAmount || 0,
            Remarks: purchaseData.Remarks || '',
            IsActive: purchaseData.IsActive ?? true,
            IsVerified: purchaseData.IsVerified ?? false,
            IsConvertToGoodReceipt: purchaseData.IsConvertToGoodReceipt ?? false,
            CreatedBy: purchaseData.CreatedBy,
            CreatedByName: purchaseData.CreatedByName || '',
            CreatedDate: purchaseData.CreatedDate,
            ModifiedBy: purchaseData.ModifiedBy,
            ModifiedByName: purchaseData.ModifiedByName || '',
            ModifiedDate: purchaseData.ModifiedDate
          });

          setDetailsData(details.map(d => ({
            Id: d.Id || 0,
            PurchaseId: d.PurchaseId || 0,
            CompanyId: d.CompanyId || companyId,
            BranchId: d.BranchId || branchId,
            ItemId: d.ItemId,
            ItemName: d.ItemName || '',
            BatchNumber: d.BatchNumber || '',
            PurchaseOrderId: d.PurchaseOrderId || null,
            OrderQuantity: d.OrderQuantity || 0,
            ReceivedQuantity: d.ReceivedQuantity || 0,
            BilledQuantity: d.BilledQuantity || 0,
            DamageQuantity: d.DamageQuantity || 0,
            ExcessQuantity: d.ExcessQuantity || 0,
            LessQuantity: d.LessQuantity || 0,
            StockQuantity: d.StockQuantity || 0,
            ReturnDamageQuantity: d.ReturnDamageQuantity || 0,
            ReturnStockQuantity: d.ReturnStockQuantity || 0,
            Rate: d.Rate || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            HSNCode: d.HSNCode || '',
            VATRate: d.VATRate || d.GSTRate || 0,
            TaxableAmount: d.TaxableAmount || 0,
            VATAmount: d.VATAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy,
            CreatedDate: d.CreatedDate,
            ModifiedBy: d.ModifiedBy,
            ModifiedDate: d.ModifiedDate
          })));

        } else if (activity.type === 'purchasereturn') {
          const returnData = data.PurchaseReturn || data;
          const details = data.PurchaseReturnDetails || [];

          setSelectedPurchaseReturn(returnData);
          setPurchaseReturnDetails(details);
          setPurchaseReturnViewModalOpen(true);

        } else if (activity.type === 'salereturn') {
          const returnData = data.SaleReturn || data;
          const details = data.SaleReturnDetails || [];

          // Ensure all required fields are present
          const completeReturnData = {
            ...returnData,
            MasterName: returnData.MasterName || returnData.CustomerName || '',
            CustomerName: returnData.CustomerName || returnData.MasterName || '',
            VATNumber: returnData.VATNumber || returnData.GSTNumber || '',
            InvoiceTypeName: returnData.InvoiceTypeName || '',
            BillNumber: returnData.BillNumber || '',
            BillDate: returnData.BillDate || '',
            SaleNo: returnData.SaleNo || '',
            VATCalculationType: returnData.VATCalculationType || 1,
            FreightAmount: returnData.FreightAmount || 0,
            PackingAmount: returnData.PackingAmount || 0,
            TCSAmount: returnData.TCSAmount || 0,
            CashAmount: returnData.CashAmount || 0,
            UPIAmount: returnData.UPIAmount || 0,
            CardAmount: returnData.CardAmount || 0,
            BankId: returnData.BankId || '',
            Remarks: returnData.Remarks || ''
          };

          setSelectedSaleReturn(completeReturnData);
          setSaleReturnDetails(details);
          setSaleReturnViewModalOpen(true);

        } else if (activity.type === 'payment') {
          const paymentData = data.PaymentAndReceipt || data;

          setFormData({
            Id: paymentData.Id,
            CompanyId: paymentData.CompanyId || companyId,
            BranchId: paymentData.BranchId || branchId,
            MasterId: paymentData.MasterId,
            BillNumber: paymentData.BillNumber,
            BillDate: paymentData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            IsType: paymentData.IsType,
            TotalAmount: paymentData.TotalAmount || paymentData.Amount || 0,
            Remarks: paymentData.Remarks || ''
          });

          setDetailsData([]);

        } else if (activity.type === 'journal') {
          const journalData = data.JournalEntry || data;
          const details = data.JournalEntryDetails || [];

          // Ensure all required fields are present
          const completeJournalData = {
            Id: journalData.Id,
            CompanyId: journalData.CompanyId || companyId,
            BranchId: journalData.BranchId || branchId,
            BillNumber: journalData.BillNumber || '',
            BillPrefix: journalData.BillPrefix || '',
            FinYear: journalData.FinYear || '',
            BillDate: journalData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            Narration: journalData.Narration || '',
            TotalAmount: journalData.TotalAmount || journalData.Amount || 0,
            TotalChildAmount: journalData.TotalChildAmount || 0,
            IsActive: journalData.IsActive ?? true,
            CreatedBy: journalData.CreatedBy,
            CreatedDate: journalData.CreatedDate,
            CreatedByName: journalData.CreatedByName || '',
            ModifiedBy: journalData.ModifiedBy,
            ModifiedDate: journalData.ModifiedDate,
            ModifiedByName: journalData.ModifiedByName || ''
          };

          // Map journal entry details with complete fields
          const completeDetails = details.map(detail => ({
            Id: detail.Id || 0,
            JournalEntryId: detail.JournalEntryId || journalData.Id,
            MasterId: detail.MasterId || detail.AccountHeadId || 0,
            MasterName: detail.MasterName || detail.AccountHeadName || '',
            Amount: detail.Amount || 0,
            IsDebit: detail.IsDebit ?? true,
            Remarks: detail.Remarks || '',
            IsActive: detail.IsActive ?? true,
            CreatedBy: detail.CreatedBy,
            CreatedDate: detail.CreatedDate,
            ModifiedBy: detail.ModifiedBy,
            ModifiedDate: detail.ModifiedDate
          }));

          setFormData(completeJournalData);
          setDetailsData(completeDetails);
        }

        setOpenModal(true);
      }
    } catch (error) {
      console.error('Error fetching activity details:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalType('');
    setFormData({});
    setDetailsData([]);
    setRelatedItems([]);
  };

  const handleSave = () => {
    // Not needed for view mode, but required prop
    handleCloseModal();
  };

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="professional-dashboard">
      {/* Header */}
      {/* <div className="dash-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dash-subtitle">{companyName} • {username}</p>
        </div>
        <button className="refresh-button" onClick={fetchDashboardData}>
          <Refresh /> Refresh
        </button>
      </div>*/}

      {/* Transaction Cards Grid - Compact */}
      <div className="transaction-grid">
        <div className="transaction-card purple" onClick={() => handleCardClick('sale')}>
          <div className="card-header">
            <Receipt className="card-icon" />
            <span className="card-badge">{transactionStats.sales.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Sales</span>
            <p className="card-amount">{formatCurrency(transactionStats.sales.amount)}</p>
          </div>
        </div>

        <div className="transaction-card yellow" onClick={() => handleCardClick('purchase')}>
          <div className="card-header">
            <ShoppingCart className="card-icon" />
            <span className="card-badge">{transactionStats.purchases.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Purchases</span>
            <p className="card-amount">{formatCurrency(transactionStats.purchases.amount)}</p>
          </div>
        </div>

        <div className="transaction-card purple-light" onClick={() => handleCardClick('salereturn')}>
          <div className="card-header">
            <Undo className="card-icon" />
            <span className="card-badge">{transactionStats.saleReturns.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Sale Returns</span>
            <p className="card-amount">{formatCurrency(transactionStats.saleReturns.amount)}</p>
          </div>
        </div>

        <div className="transaction-card yellow-light" onClick={() => handleCardClick('purchasereturn')}>
          <div className="card-header">
            <Undo className="card-icon" />
            <span className="card-badge">{transactionStats.purchaseReturns.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Purchase Returns</span>
            <p className="card-amount">{formatCurrency(transactionStats.purchaseReturns.amount)}</p>
          </div>
        </div>

        <div className="transaction-card purple" onClick={() => handleCardClick('payment')}>
          <div className="card-header">
            <Payment className="card-icon" />
            <span className="card-badge">{transactionStats.paymentReceipts.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Payment/Receipt</span>
            <p className="card-amount">{formatCurrency(transactionStats.paymentReceipts.amount)}</p>
          </div>
        </div>

        <div className="transaction-card yellow" onClick={() => handleCardClick('journal')}>
          <div className="card-header">
            <Description className="card-icon" />
            <span className="card-badge">{transactionStats.journalEntries.count}</span>
          </div>
          <div className="card-info">
            <span className="card-label">Journal Entry</span>
            <p className="card-amount">{formatCurrency(transactionStats.journalEntries.amount)}</p>
          </div>
        </div>
      </div>

      {/* Recent Activities and Sales Graph */}
      <div className="dashboard-bottom-section">
        <div className="activities-container">
          <div className="activities-header">
            <h2>Recent Activity History</h2>
            <span className="activities-badge">{recentActivities.length} transactions</span>
          </div>

          <div className="activities-timeline">
            {recentActivities.map((activity, index) => (
              <div
                key={index}
                className="timeline-item"
                style={{ borderLeftColor: activity.color, cursor: 'pointer' }}
                onClick={() => handleActivityClick(activity)}
              >
                <div className="timeline-icon" style={{ backgroundColor: activity.color }}>
                  {activity.icon}
                </div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-title">{activity.title}</span>
                    <span className="timeline-amount" style={{ color: activity.color }}>
                      {formatCurrency(activity.amount)}
                    </span>
                  </div>
                  <p className="timeline-description">{activity.description}</p>
                  <div className="timeline-footer">
                    <span>{formatDate(activity.date)}</span>
                    <span>{activity.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sales Graph */}
        <div className="sales-graph-container">
          <div className="graph-header">
            <h2>Daily Sales Trend</h2>
            <span className="graph-badge">Last 30 Days</span>
          </div>

          <div className="graph-content">
            <div className="graph-area">
              <svg className="wave-graph" viewBox="0 0 800 300" preserveAspectRatio="none">
                <defs>
                  {/* Power BI style gradient for area fill */}
                  <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#00bcf2', stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: '#00bcf2', stopOpacity: 0.05 }} />
                  </linearGradient>

                  {/* Solid line color - Power BI Blue */}
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: '#00bcf2' }} />
                    <stop offset="100%" style={{ stopColor: '#00bcf2' }} />
                  </linearGradient>

                  {/* Minimal shadow */}
                  <filter id="shadow">
                    <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.1" />
                  </filter>
                </defs>

                {/* Grid lines - Power BI style */}
                <defs>
                  <pattern id="grid" width="80" height="60" patternUnits="userSpaceOnUse">
                    <path d="M 80 0 L 0 0 0 60" fill="none" stroke="#e0e0e0" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="800" height="300" fill="url(#grid)" />

                {/* Generate smooth wave path with animation */}
                <path
                  className="graph-fill-path"
                  d={(() => {
                    if (dailySales.length === 0) return '';

                    const maxAmount = Math.max(...dailySales.map(d => d.amount), 1);
                    const width = 800;
                    const height = 300;
                    const padding = 20;
                    const stepX = width / (dailySales.length - 1);

                    let path = `M 0,${height}`;

                    dailySales.forEach((day, index) => {
                      const x = index * stepX;
                      const y = height - padding - ((day.amount / maxAmount) * (height - padding * 2));

                      if (index === 0) {
                        path += ` L ${x},${y}`;
                      } else {
                        const prevX = (index - 1) * stepX;
                        const prevY = height - padding - ((dailySales[index - 1].amount / maxAmount) * (height - padding * 2));
                        const cpX1 = prevX + (x - prevX) / 2;
                        const cpY1 = prevY;
                        const cpX2 = prevX + (x - prevX) / 2;
                        const cpY2 = y;
                        path += ` C ${cpX1},${cpY1} ${cpX2},${cpY2} ${x},${y}`;
                      }
                    });

                    path += ` L ${width},${height} Z`;
                    return path;
                  })()}
                  fill="url(#waveGradient)"
                  stroke="none"
                  filter="url(#shadow)"
                />

                {/* Smooth wave line on top with gradient and glow */}
                <path
                  className="graph-line-path"
                  d={(() => {
                    if (dailySales.length === 0) return '';

                    const maxAmount = Math.max(...dailySales.map(d => d.amount), 1);
                    const width = 800;
                    const height = 300;
                    const padding = 20;
                    const stepX = width / (dailySales.length - 1);

                    let path = '';

                    dailySales.forEach((day, index) => {
                      const x = index * stepX;
                      const y = height - padding - ((day.amount / maxAmount) * (height - padding * 2));

                      if (index === 0) {
                        path += `M ${x},${y}`;
                      } else {
                        const prevX = (index - 1) * stepX;
                        const prevY = height - padding - ((dailySales[index - 1].amount / maxAmount) * (height - padding * 2));
                        const cpX1 = prevX + (x - prevX) / 2;
                        const cpY1 = prevY;
                        const cpX2 = prevX + (x - prevX) / 2;
                        const cpY2 = y;
                        path += ` C ${cpX1},${cpY1} ${cpX2},${cpY2} ${x},${y}`;
                      }
                    });

                    return path;
                  })()}
                  fill="none"
                  stroke="url(#lineGradient)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Animated data points with gradient colors */}
                {dailySales.map((day, index) => {
                  const maxAmount = Math.max(...dailySales.map(d => d.amount), 1);
                  const width = 800;
                  const height = 300;
                  const padding = 20;
                  const stepX = width / (dailySales.length - 1);
                  const x = index * stepX;
                  const y = height - padding - ((day.amount / maxAmount) * (height - padding * 2));

                  // Power BI Blue color for all points
                  const pointColor = '#00bcf2';

                  return (
                    <g key={index} className="graph-point">
                      {/* Main point - Power BI style */}
                      <circle
                        cx={x}
                        cy={y}
                        r="3.5"
                        fill="#ffffff"
                        stroke={pointColor}
                        strokeWidth="2"
                        style={{ animationDelay: `${index * 0.05}s` }}
                        className="data-point"
                      >
                        <title>{`${day.label}: ${formatCurrency(day.amount)}`}</title>
                      </circle>
                    </g>
                  );
                })}
              </svg>

              {/* X-axis labels */}
              <div className="graph-x-axis">
                {dailySales.filter((_, index) => index % 5 === 0).map((day, index) => (
                  <span key={index} className="x-label">{day.label}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Graph Stats with animation */}
          <div className="graph-stats">
            <div className="stat-item stat-item-1">
              <span className="stat-label">Total Sales</span>
              <span className="stat-value">{formatCurrency(dailySales.reduce((sum, day) => sum + day.amount, 0))}</span>
            </div>
            <div className="stat-item stat-item-2">
              <span className="stat-label">Average/Day</span>
              <span className="stat-value">{formatCurrency(dailySales.reduce((sum, day) => sum + day.amount, 0) / dailySales.length || 0)}</span>
            </div>
            <div className="stat-item stat-item-3">
              <span className="stat-label">Peak Day</span>
              <span className="stat-value">{formatCurrency(Math.max(...dailySales.map(d => d.amount)))}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="quick-actions-right">
          <div className="quick-actions-header">
            <h2>Quick Actions</h2>
          </div>



          {/* DSS Cards Row */}
          <div className="dss-cards-row">
            <div className="dss-card dss-summary">
              <div className="dss-card-icon">
                <Description />
              </div>
              <div className="dss-card-content">
                <span className="dss-card-label">DSS Summary</span>
                <span className="dss-card-value">View Report</span>
              </div>
            </div>

            <div className="dss-card dss-payment">
              <div className="dss-card-icon">
                <Payment />
              </div>
              <div className="dss-card-content">
                <span className="dss-card-label">DSS Payment Wise</span>
                <span className="dss-card-value">View Details</span>
              </div>
            </div>

            <div className="dss-card dss-due">
              <div className="dss-card-icon">
                <Receipt />
              </div>
              <div className="dss-card-content">
                <span className="dss-card-label">DSS Due List</span>
                <span className="dss-card-value">View Dues</span>
              </div>
            </div>
            {/* <div className="transaction-card yellow-light" onClick={() => handleCardClick('purchasereturn')}></div> */}
            
            <div className="dss-card dss-cashbook">
              <div className="dss-card-icon">
                <AccountBalanceWallet />
              </div>
              <div className="dss-card-content" onClick={() => handleReportCardClick('cashbook')}>
                <span className="dss-card-label">Cash Book</span>
                <span className="dss-card-value">View Details</span>
              </div>
            </div>

            <div className="dss-card dss-ledger" onClick={() => handleReportCardClick('ledger')}>
              <div className="dss-card-icon">
                <AccountBalance />
              </div>
              <div className="dss-card-content">
                <span className="dss-card-label">Ledger</span>
                <span className="dss-card-value">View Details</span>
              </div>
            </div>

          </div>

          {/* Financial Cards Row */}
          <div className="financial-cards-row">
            <div className="financial-card payables">
              <div className="financial-card-header">
                <ShoppingCart className="financial-icon" />
                <span className="financial-label">Payables(Pending)</span>
              </div>
              <div className="financial-card-body">
                <span className="financial-title">Total Purchase Balance</span>
                <span className="financial-amount">{formatCurrency(transactionStats.purchases.amount - (transactionStats.purchaseReturns.amount || 0))}</span>
                <span className="financial-subtitle">Amount to Pay</span>
              </div>
            </div>

            <div className="financial-card receivables">
              <div className="financial-card-header">
                <Receipt className="financial-icon" />
                <span className="financial-label">Receivables</span>
              </div>
              <div className="financial-card-body">
                <span className="financial-title">Total Sale Balance</span>
                <span className="financial-amount">{formatCurrency(transactionStats.sales.amount - (transactionStats.saleReturns.amount || 0))}</span>
                <span className="financial-subtitle">Amount to Receive (Monthly)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {openModal && modalType === 'sale' && (
        <SaleModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          saleDetails={detailsData}
          setSaleDetails={setDetailsData}
          relatedItems={relatedItems}
          setRelatedItems={setRelatedItems}
          onSave={handleSave}
          customers={customers}
          items={items}
          banks={banks}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'purchase' && (
        <PurchaseModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          purchaseDetails={detailsData}
          setPurchaseDetails={setDetailsData}
          relatedItems={relatedItems}
          setRelatedItems={setRelatedItems}
          onSave={handleSave}
          vendors={vendors}
          items={items}
          loading={modalLoading}
          freightPackingGST={freightPackingGST}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'payment' && (
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          paymentReceiptDetails={detailsData}
          setPaymentReceiptDetails={setDetailsData}
          onSave={handleSave}
          masters={masters}
          banks={banks}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {openModal && modalType === 'journal' && (
        <JournalEntryModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          journalEntryDetails={detailsData}
          setJournalEntryDetails={setDetailsData}
          onSave={handleSave}
          masters={masters}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Purchase Return View Modal */}
      <Dialog
        open={purchaseReturnViewModalOpen}
        onClose={() => setPurchaseReturnViewModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#fafbfc',
          p: 2,
          pb: 1.5
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#0f172a', fontSize: '0.875rem' }}>
                  View Purchase Return
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                  Purchase return details and items
                </Typography>
              </Box>
              {selectedPurchaseReturn?.IsActive === false && (
                <Chip
                  label="INACTIVE"
                  size="small"
                  sx={{
                    bgcolor: '#991b1b',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: '20px',
                    letterSpacing: '0.5px'
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={() => setPurchaseReturnViewModalOpen(false)}
              size="small"
              sx={{
                bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fecaca' : '#f1f5f9',
                '&:hover': { bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fca5a5' : '#e2e8f0' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fef2f2' : '#fafbfc' }}>
          {selectedPurchaseReturn && (
            <>
              {/* Header Information */}
              <Box sx={{
                bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#dbeafe',
                borderRadius: 1.5,
                p: 1,
                mb: 1.5,
                border: selectedPurchaseReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                      {selectedPurchaseReturn.BillNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Return Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {formatDate(selectedPurchaseReturn.BillDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Purchase No
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedPurchaseReturn.PurchaseNo || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Supplier
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.75rem' }}>
                      {selectedPurchaseReturn.MasterName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      VAT Type
                    </Typography>
                    <Chip
                      label={selectedPurchaseReturn.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax'}
                      size="small"
                      sx={{
                        bgcolor: selectedPurchaseReturn.VATCalculationType === 2 ? '#dcfce7' : '#dbeafe',
                        color: selectedPurchaseReturn.VATCalculationType === 2 ? '#166534' : '#1e40af',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={1.75}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                      Invoice Type
                    </Typography>
                    <Chip
                      label={selectedPurchaseReturn.InvoiceTypeName || 'N/A'}
                      size="small"
                      sx={{
                        bgcolor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: '20px'
                      }}
                    />
                  </Grid>
                  {selectedPurchaseReturn.Remarks && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.2, display: 'block', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Remarks
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem', fontStyle: 'italic' }}>
                        {selectedPurchaseReturn.Remarks}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Items
                </Typography>
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ bgcolor: selectedPurchaseReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderBottom: selectedPurchaseReturn?.IsActive === false ? '2px solid #fca5a5' : '2px solid #93c5fd' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>#</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>ITEM NAME</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>HSN CODE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>QUANTITY</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>RATE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>AMOUNT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>DISC AMT</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT %</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TAXABLE</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>VAT Amount</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>IGST</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: selectedPurchaseReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.6rem', textTransform: 'uppercase' }}>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseReturnDetails.map((item, index) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                {index + 1}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.7rem' }}>
                                {item.ItemName}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.HSNCode || '-'}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={item.Quantity}
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {formatCurrency(item.Rate)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.Amount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {item.DiscountPercentage || 0}%
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.DiscountAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <Chip
                                label={`${item.VATRate || item.GSTRate || 0}%`}
                                size="small"
                                sx={{
                                  bgcolor: '#d1fae5',
                                  color: '#065f46',
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  minWidth: '45px',
                                  height: '20px'
                                }}
                              />
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TaxableAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.VATAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.7rem', fontWeight: 600 }}>
                                {formatCurrency(item.IGSTAmount || 0)}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.7rem' }}>
                                {formatCurrency(item.TotalAmount || 0)}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Return Summary Section */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.75, fontSize: '0.8rem' }}>
                  Return Summary
                </Typography>
                <Grid container spacing={1.5}>
                  {/* Financial Summary */}
                  <Grid item xs={12} md={8}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Subtotal
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.Amount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Discount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.DiscountAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Taxable Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.TaxableAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              VAT Amount
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.VATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#fee2e2', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#991b1b', fontSize: '0.6rem', fontWeight: 600 }}>
                              IGST
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.IGSTAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.FreightAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Freight VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.FreightVATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.PackingAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Packing VAT
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mt: 0.2, fontSize: '0.75rem' }}>
                              {formatCurrency(selectedPurchaseReturn.PackingVATAmount || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600 }}>
                              Round Off
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.2, fontSize: '0.75rem' }}>
                              {selectedPurchaseReturn.RoundOff >= 0 ? '+' : ''}{formatCurrency(selectedPurchaseReturn.RoundOff || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Total Summary */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                      p: 1,
                      border: '1px solid #e2e8f0',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dbeafe', borderRadius: 1, border: '2px solid #93c5fd' }}>
                        <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Total Return Amount
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.3, fontSize: '1.25rem' }}>
                          {formatCurrency(selectedPurchaseReturn.TotalAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fef3c7', borderRadius: 1, border: '2px solid #fde68a' }}>
                        <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Paid Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ea580c', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedPurchaseReturn.PaidAmount || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dcfce7', borderRadius: 1, border: '2px solid #86efac' }}>
                        <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
                          Balance Amount
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mt: 0.3, fontSize: '0.95rem' }}>
                          {formatCurrency(selectedPurchaseReturn.BalanceAmount || (selectedPurchaseReturn.TotalAmount - selectedPurchaseReturn.PaidAmount))}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{
          p: 1,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            variant="outlined"
            onClick={() => setPurchaseReturnViewModalOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1,
              px: 2.5,
              py: 0.5,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                bgcolor: '#eff6ff'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Sale Return View Modal */}
      <SaleReturnModal
        open={saleReturnViewModalOpen}
        onClose={() => setSaleReturnViewModalOpen(false)}
        modalAction="view"
        selectedSaleReturn={selectedSaleReturn}
        saleReturnDetails={saleReturnDetails}
      />

      {/* Modern Loading Overlay */}
      <ModernLoadingOverlay
        open={modalLoading}
        message="Loading transaction details..."
      />
    </div>
  );
};

export default Home;
